import { useState, useEffect, useRef, useCallback } from 'react';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  TrashIcon,
  UserCircleIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftIcon,
  PaperClipIcon,
  ArrowDownTrayIcon,
  DocumentIcon,
  PhotoIcon,
  MusicalNoteIcon,
  FilmIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import {
  getComments,
  addComment,
  deleteComment,
  updateTask,
  deleteTask,
  generateFutureWeeks,
  uploadTaskAttachments,
  downloadAttachment,
  deleteAttachment,
  getTaskById,
} from '../api/monday-meeting.api';
import type {
  MondayMeetingTask,
  MondayMeetingComment,
  MondayMeetingAttachment,
  MondayMeetingCategory,
  User,
  TaskStatus,
  TaskPriority,
} from '../types/monday-meeting.types';
import './TaskDetailModal.css';

interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: MondayMeetingTask;
  onUpdate: () => void;
  users: User[];
  categories: MondayMeetingCategory[];
}

const STATUS_OPTIONS: { value: TaskStatus; label: string; color: string }[] = [
  { value: 'pending', label: 'En attente', color: '#f59e0b' },
  { value: 'in_progress', label: 'En cours', color: '#3b82f6' },
  { value: 'completed', label: 'Terminé', color: '#10b981' },
  { value: 'cancelled', label: 'Annulé', color: '#6b7280' },
];

const PRIORITY_OPTIONS: { value: TaskPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Basse', color: '#6b7280' },
  { value: 'medium', label: 'Moyenne', color: '#f59e0b' },
  { value: 'high', label: 'Haute', color: '#ef4444' },
];

export function TaskDetailModal({
  isOpen,
  onClose,
  task,
  onUpdate,
  users,
  categories = [],
}: TaskDetailModalProps) {
  const [comments, setComments] = useState<MondayMeetingComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const [priority, setPriority] = useState<TaskPriority>(task.priority);
  const [categoryId, setCategoryId] = useState<string | null>(task.category_id || null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>(
    task.assignees.map((a) => a.user_id)
  );
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showDueWeekDropdown, setShowDueWeekDropdown] = useState(false);
  const [dueWeek, setDueWeek] = useState({ year: task.due_year, week: task.due_week });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Attachment state
  const [attachments, setAttachments] = useState<MondayMeetingAttachment[]>(task.attachments || []);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Preview state
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Generate future weeks for the dropdown (12 weeks ahead)
  const futureWeeks = generateFutureWeeks(12);

  const commentsEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && task) {
      // Fetch full task with attachments
      const loadFullTask = async () => {
        try {
          const response = await getTaskById(task.id);
          if (response.success && response.data) {
            setAttachments(response.data.attachments || []);
          }
        } catch (error) {
          console.error('Error loading task attachments:', error);
          setAttachments(task.attachments || []);
        }
      };

      loadFullTask();
      loadComments();
      setStatus(task.status);
      setPriority(task.priority);
      setCategoryId(task.category_id || null);
      setDueWeek({ year: task.due_year, week: task.due_week });
      setSelectedAssignees(task.assignees.map((a) => a.user_id));
      setIsClosing(false);
      setShowDeleteConfirm(false);
      setShowDueWeekDropdown(false);
      setShowCategoryDropdown(false);
    }
  }, [isOpen, task]);

  useEffect(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 250);
  };

  const loadComments = async () => {
    setLoading(true);
    try {
      const response = await getComments(task.id);
      if (response.success) {
        setComments(response.data);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendComment = async () => {
    if (!newComment.trim()) return;

    setSendingComment(true);
    try {
      const response = await addComment(task.id, newComment.trim());
      if (response.success) {
        setComments((prev) => [...prev, response.data]);
        setNewComment('');
        onUpdate();
      }
    } catch (error) {
      console.error('Error sending comment:', error);
    } finally {
      setSendingComment(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendComment();
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      onUpdate();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleStatusChange = async (newStatus: TaskStatus) => {
    try {
      await updateTask(task.id, { status: newStatus });
      setStatus(newStatus);
      setShowStatusDropdown(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handlePriorityChange = async (newPriority: TaskPriority) => {
    try {
      await updateTask(task.id, { priority: newPriority });
      setPriority(newPriority);
      setShowPriorityDropdown(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  const handleCategoryChange = async (newCategoryId: string | null) => {
    try {
      await updateTask(task.id, { category_id: newCategoryId });
      setCategoryId(newCategoryId);
      setShowCategoryDropdown(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleDueWeekChange = async (year: number, week: number) => {
    try {
      await updateTask(task.id, { due_year: year, due_week: week });
      setDueWeek({ year, week });
      setShowDueWeekDropdown(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating due week:', error);
    }
  };

  const toggleAssignee = async (userId: string) => {
    const newAssignees = selectedAssignees.includes(userId)
      ? selectedAssignees.filter((id) => id !== userId)
      : [...selectedAssignees, userId];

    try {
      await updateTask(task.id, { assignee_ids: newAssignees });
      setSelectedAssignees(newAssignees);
      onUpdate();
    } catch (error) {
      console.error('Error updating assignees:', error);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteTask(task.id);
      setShowDeleteConfirm(false);
      onClose();
      onUpdate();
    } catch (error) {
      console.error('Error deleting task:', error);
    } finally {
      setDeleting(false);
    }
  };

  // =========================================================================
  // Attachment Handlers
  // =========================================================================

  const handleFileUpload = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    setUploadingFiles(true);
    setUploadProgress(0);

    try {
      const response = await uploadTaskAttachments(task.id, fileArray, (percent) => {
        setUploadProgress(percent);
      });

      if (response.success) {
        setAttachments(prev => [...prev, ...response.data]);
        onUpdate();
      }
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setUploadingFiles(false);
      setUploadProgress(0);
    }
  }, [task.id, onUpdate]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileUpload(e.target.files);
      e.target.value = ''; // Reset input
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleDownloadAttachment = async (attachment: MondayMeetingAttachment) => {
    try {
      await downloadAttachment(attachment.id, attachment.file_name);
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    setDeletingAttachment(attachmentId);
    try {
      await deleteAttachment(attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      onUpdate();
    } catch (error) {
      console.error('Error deleting attachment:', error);
    } finally {
      setDeletingAttachment(null);
    }
  };

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return PhotoIcon;
    if (contentType.startsWith('audio/')) return MusicalNoteIcon;
    if (contentType.startsWith('video/')) return FilmIcon;
    if (contentType.includes('pdf') || contentType.includes('document') || contentType.includes('word')) {
      return DocumentTextIcon;
    }
    return DocumentIcon;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Preview helpers
  const isPreviewable = (contentType: string): boolean => {
    return contentType.startsWith('image/') || contentType === 'application/pdf';
  };

  const isImageFile = (contentType: string): boolean => {
    return contentType.startsWith('image/');
  };

  const isPdfFile = (contentType: string): boolean => {
    return contentType === 'application/pdf';
  };

  const previewableAttachments = attachments.filter(a => isPreviewable(a.content_type));

  const loadPreview = async (index: number) => {
    const attachment = attachments[index];
    if (!attachment || !isPreviewable(attachment.content_type)) return;

    setPreviewIndex(index);
    setPreviewLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      const baseUrl = import.meta.env.VITE_API_URL || 'https://myapp.chatelainxpert.be';
      const url = `${baseUrl}/api/admin/monday-meeting/attachments/${attachment.id}/download?inline=true`;

      console.log('[Preview] Starting fetch:', { attachmentId: attachment.id, url, hasToken: !!token });

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      console.log('[Preview] Response:', { status: response.status, ok: response.ok, contentType: response.headers.get('content-type') });

      if (response.ok) {
        const blob = await response.blob();
        console.log('[Preview] Blob received:', { type: blob.type, size: blob.size });
        const blobUrl = URL.createObjectURL(blob);
        console.log('[Preview] Blob URL created:', blobUrl);
        setPreviewUrl(blobUrl);
      } else {
        console.error('[Preview] Fetch failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('[Preview] Error loading preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewIndex(null);
    setPreviewUrl(null);
  };

  const navigatePreview = (direction: 'prev' | 'next') => {
    if (previewIndex === null) return;

    const currentAttachment = attachments[previewIndex];
    const previewableIndices = attachments
      .map((a, i) => isPreviewable(a.content_type) ? i : -1)
      .filter(i => i !== -1);

    const currentPreviewableIndex = previewableIndices.indexOf(previewIndex);
    let newPreviewableIndex: number;

    if (direction === 'prev') {
      newPreviewableIndex = currentPreviewableIndex > 0 ? currentPreviewableIndex - 1 : currentPreviewableIndex;
    } else {
      newPreviewableIndex = currentPreviewableIndex < previewableIndices.length - 1 ? currentPreviewableIndex + 1 : currentPreviewableIndex;
    }

    const newIndex = previewableIndices[newPreviewableIndex];
    if (newIndex !== previewIndex) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      loadPreview(newIndex);
    }
  };

  // Keyboard navigation for preview
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (previewIndex === null) return;

      if (e.key === 'Escape') {
        closePreview();
      } else if (e.key === 'ArrowLeft') {
        navigatePreview('prev');
      } else if (e.key === 'ArrowRight') {
        navigatePreview('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewIndex, attachments]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, []);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-BE', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFullDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-BE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusOption = (value: TaskStatus) =>
    STATUS_OPTIONS.find((o) => o.value === value) || STATUS_OPTIONS[0];

  const getPriorityOption = (value: TaskPriority) =>
    PRIORITY_OPTIONS.find((o) => o.value === value) || PRIORITY_OPTIONS[1];

  if (!isOpen) return null;

  const currentStatus = getStatusOption(status);
  const currentPriority = getPriorityOption(priority);

  return (
    <div className={`tdm-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        ref={panelRef}
        className={`tdm-panel ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tdm-header">
          <div className="tdm-header-top">
            <div className="tdm-task-id">
              <span className="tdm-number">#{task.task_number}</span>
              {task.is_late && (
                <span className="tdm-late-badge">
                  <ExclamationTriangleIcon />
                  En retard
                </span>
              )}
            </div>
            <button className="tdm-close-btn" onClick={handleClose}>
              <XMarkIcon />
            </button>
          </div>
          <h1 className="tdm-title">{task.title}</h1>
        </div>

        {/* Quick Actions Bar */}
        <div className="tdm-quick-bar">
            {/* Status */}
            <div className="tdm-dropdown-container">
              <button
                className="tdm-badge-btn"
                style={{ '--badge-color': currentStatus.color } as React.CSSProperties}
                onClick={() => {
                  setShowStatusDropdown(!showStatusDropdown);
                  setShowPriorityDropdown(false);
                  setShowCategoryDropdown(false);
                  setShowDueWeekDropdown(false);
                  setShowAssigneeDropdown(false);
                }}
              >
                <CheckCircleIcon />
                {currentStatus.label}
              </button>
              {showStatusDropdown && (
                <div className="tdm-dropdown">
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      className={`tdm-dropdown-item ${status === opt.value ? 'active' : ''}`}
                      style={{ '--item-color': opt.color } as React.CSSProperties}
                      onClick={() => handleStatusChange(opt.value)}
                    >
                      <span className="tdm-dropdown-dot" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Priority */}
            <div className="tdm-dropdown-container">
              <button
                className="tdm-badge-btn"
                style={{ '--badge-color': currentPriority.color } as React.CSSProperties}
                onClick={() => {
                  setShowPriorityDropdown(!showPriorityDropdown);
                  setShowStatusDropdown(false);
                  setShowCategoryDropdown(false);
                  setShowDueWeekDropdown(false);
                  setShowAssigneeDropdown(false);
                }}
              >
                <ExclamationTriangleIcon />
                {currentPriority.label}
              </button>
              {showPriorityDropdown && (
                <div className="tdm-dropdown">
                  {PRIORITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      className={`tdm-dropdown-item ${priority === opt.value ? 'active' : ''}`}
                      style={{ '--item-color': opt.color } as React.CSSProperties}
                      onClick={() => handlePriorityChange(opt.value)}
                    >
                      <span className="tdm-dropdown-dot" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Category (Sujet) */}
            <div className="tdm-dropdown-container">
              <button
                className="tdm-badge-btn"
                style={{ '--badge-color': '#059669' } as React.CSSProperties}
                onClick={() => {
                  setShowCategoryDropdown(!showCategoryDropdown);
                  setShowStatusDropdown(false);
                  setShowPriorityDropdown(false);
                  setShowDueWeekDropdown(false);
                  setShowAssigneeDropdown(false);
                }}
              >
                <TagIcon />
                {categoryId
                  ? categories.find((c) => c.id === categoryId)?.name || 'Sujet'
                  : 'Sujet'}
              </button>
              {showCategoryDropdown && (
                <div className="tdm-dropdown">
                  <button
                    className={`tdm-dropdown-item ${!categoryId ? 'active' : ''}`}
                    style={{ '--item-color': '#9ca3af' } as React.CSSProperties}
                    onClick={() => handleCategoryChange(null)}
                  >
                    <span className="tdm-dropdown-dot" />
                    Aucun
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      className={`tdm-dropdown-item ${categoryId === cat.id ? 'active' : ''}`}
                      style={{ '--item-color': '#059669' } as React.CSSProperties}
                      onClick={() => handleCategoryChange(cat.id)}
                    >
                      <span className="tdm-dropdown-dot" />
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Due Week */}
            <div className="tdm-dropdown-container">
              <button
                className={`tdm-badge-btn ${task.is_late ? 'tdm-badge-late' : ''}`}
                style={{ '--badge-color': task.is_late ? '#ef4444' : '#3b82f6' } as React.CSSProperties}
                onClick={() => {
                  setShowDueWeekDropdown(!showDueWeekDropdown);
                  setShowStatusDropdown(false);
                  setShowPriorityDropdown(false);
                  setShowCategoryDropdown(false);
                  setShowAssigneeDropdown(false);
                }}
              >
                <CalendarIcon />
                S{dueWeek.week}
              </button>
              {showDueWeekDropdown && (
                <div className="tdm-dropdown tdm-dropdown-weeks">
                  {futureWeeks.map((w) => (
                    <button
                      key={`${w.year}-${w.week}`}
                      className={`tdm-dropdown-item ${dueWeek.year === w.year && dueWeek.week === w.week ? 'active' : ''}`}
                      onClick={() => handleDueWeekChange(w.year, w.week)}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Assignees */}
            <div className="tdm-dropdown-container">
              <button
                className="tdm-badge-btn"
                style={{ '--badge-color': '#8b5cf6' } as React.CSSProperties}
                onClick={() => {
                  setShowAssigneeDropdown(!showAssigneeDropdown);
                  setShowStatusDropdown(false);
                  setShowPriorityDropdown(false);
                  setShowCategoryDropdown(false);
                  setShowDueWeekDropdown(false);
                }}
              >
                <UserIcon />
                {selectedAssignees.length === 0
                  ? 'Non assigné'
                  : selectedAssignees.length === 1
                    ? users.find((u) => u.id === selectedAssignees[0])?.name?.split(' ')[0] || 'Assigné'
                    : `${selectedAssignees.length} assignés`}
              </button>
              {showAssigneeDropdown && (
                <div className="tdm-dropdown tdm-dropdown-assignees">
                  {users.map((user) => (
                    <label key={user.id} className="tdm-dropdown-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedAssignees.includes(user.id)}
                        onChange={() => toggleAssignee(user.id)}
                      />
                      <UserCircleIcon />
                      <span className="tdm-dropdown-user-name">{user.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
        </div>

        {/* Content */}
        <div className="tdm-content">
          {/* Description */}
          {task.description && (
            <div className="tdm-section">
              <p className="tdm-description">{task.description}</p>
            </div>
          )}

          {/* Attachments Section */}
          <div className="tdm-attachments-section">
            <div className="tdm-attachments-header">
              <PaperClipIcon />
              Pièces jointes
              <span className="tdm-attachments-count">{attachments.length}</span>
            </div>

            {/* Attachment List */}
            {attachments.length > 0 && (
              <div className="tdm-attachment-list">
                {attachments.map((attachment, index) => {
                  const FileIcon = getFileIcon(attachment.content_type);
                  const canPreview = isPreviewable(attachment.content_type);
                  return (
                    <div
                      key={attachment.id}
                      className={`tdm-attachment-item ${canPreview ? 'previewable' : ''}`}
                      onClick={canPreview ? () => loadPreview(index) : undefined}
                    >
                      <div className="tdm-attachment-icon">
                        <FileIcon />
                      </div>
                      <div className="tdm-attachment-info">
                        <span className="tdm-attachment-name" title={attachment.file_name}>
                          {attachment.file_name}
                        </span>
                        <span className="tdm-attachment-size">
                          {formatFileSize(attachment.file_size)}
                          {canPreview && <span className="tdm-preview-hint"> • Cliquer pour aperçu</span>}
                        </span>
                      </div>
                      <div className="tdm-attachment-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="tdm-attachment-btn tdm-attachment-download"
                          onClick={() => handleDownloadAttachment(attachment)}
                          title="Télécharger"
                        >
                          <ArrowDownTrayIcon />
                        </button>
                        <button
                          className="tdm-attachment-btn tdm-attachment-delete"
                          onClick={() => handleDeleteAttachment(attachment.id)}
                          disabled={deletingAttachment === attachment.id}
                          title="Supprimer"
                        >
                          {deletingAttachment === attachment.id ? (
                            <div className="tdm-spinner-small" />
                          ) : (
                            <TrashIcon />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Upload Dropzone */}
            <div
              className={`tdm-dropzone ${isDragging ? 'dragging' : ''} ${uploadingFiles ? 'uploading' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              {uploadingFiles ? (
                <div className="tdm-upload-progress">
                  <div className="tdm-spinner" />
                  <span>Téléchargement... {uploadProgress}%</span>
                  <div className="tdm-progress-bar">
                    <div
                      className="tdm-progress-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <PaperClipIcon className="tdm-dropzone-icon" />
                  <span className="tdm-dropzone-text">
                    Glissez des fichiers ici ou cliquez pour parcourir
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="tdm-metadata">
            <div className="tdm-meta-item">
              <ClockIcon />
              <div className="tdm-meta-content">
                <span className="tdm-meta-label">Créé le</span>
                <span className="tdm-meta-value">{formatFullDate(task.created_at)}</span>
              </div>
            </div>

            <div className="tdm-meta-item">
              <UserIcon />
              <div className="tdm-meta-content">
                <span className="tdm-meta-label">Créé par</span>
                <span className="tdm-meta-value">{task.created_by_name}</span>
              </div>
            </div>
          </div>

          {/* Comments */}
          <div className="tdm-comments-section">
            <div className="tdm-comments-header">
              <ChatBubbleLeftIcon />
              Commentaires
              <span className="tdm-comments-count">{comments.length}</span>
            </div>

            <div className="tdm-comments-list">
              {loading ? (
                <div className="tdm-comments-loading">
                  <div className="tdm-spinner" />
                  <span>Chargement...</span>
                </div>
              ) : comments.length === 0 ? (
                <div className="tdm-comments-empty">
                  <ChatBubbleLeftIcon />
                  <span>Aucun commentaire</span>
                </div>
              ) : (
                <>
                  {comments.map((comment) => (
                    <div key={comment.id} className="tdm-comment">
                      <div className="tdm-comment-avatar">
                        {comment.user_name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)}
                      </div>
                      <div className="tdm-comment-content">
                        <div className="tdm-comment-bubble">
                          <div className="tdm-comment-header">
                            <span className="tdm-comment-author">{comment.user_name}</span>
                            <span className="tdm-comment-time">{formatDate(comment.created_at)}</span>
                            <button
                              className="tdm-comment-delete"
                              onClick={() => handleDeleteComment(comment.id)}
                              title="Supprimer"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                          <p className="tdm-comment-text">{comment.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={commentsEndRef} />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Comment Input - Fixed at bottom */}
        <div className="tdm-comment-input-wrapper">
          <div className="tdm-comment-input-form">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Écrire un commentaire... (Entrée pour envoyer)"
              rows={1}
            />
            <button
              className="tdm-send-btn"
              onClick={handleSendComment}
              disabled={!newComment.trim() || sendingComment}
            >
              <PaperAirplaneIcon />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="tdm-footer">
          <button
            className="tdm-footer-btn tdm-footer-btn-danger"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <TrashIcon />
            Supprimer
          </button>
          <button className="tdm-footer-btn tdm-footer-btn-secondary" onClick={handleClose}>
            Fermer
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="tdm-delete-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="tdm-delete-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="tdm-delete-icon">
                <TrashIcon />
              </div>
              <h3>Supprimer cette tâche ?</h3>
              <p>Cette action est irréversible. La tâche #{task.task_number} sera définitivement supprimée.</p>
              <div className="tdm-delete-actions">
                <button
                  className="tdm-footer-btn tdm-footer-btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                >
                  Annuler
                </button>
                <button
                  className="tdm-footer-btn tdm-footer-btn-danger"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {previewIndex !== null && (
          <div className="tdm-preview-overlay" onClick={closePreview}>
            <div className="tdm-preview-container" onClick={(e) => e.stopPropagation()}>
              {/* Preview Header */}
              <div className="tdm-preview-header">
                <div className="tdm-preview-info">
                  <span className="tdm-preview-filename">
                    {attachments[previewIndex]?.file_name}
                  </span>
                  <span className="tdm-preview-counter">
                    {previewableAttachments.findIndex(a => a.id === attachments[previewIndex]?.id) + 1} / {previewableAttachments.length}
                  </span>
                </div>
                <div className="tdm-preview-actions">
                  <button
                    className="tdm-preview-action-btn"
                    onClick={() => attachments[previewIndex] && handleDownloadAttachment(attachments[previewIndex])}
                    title="Télécharger"
                  >
                    <ArrowDownTrayIcon />
                  </button>
                  <button
                    className="tdm-preview-action-btn tdm-preview-close"
                    onClick={closePreview}
                    title="Fermer (Échap)"
                  >
                    <XMarkIcon />
                  </button>
                </div>
              </div>

              {/* Preview Content */}
              <div className="tdm-preview-content">
                {previewLoading ? (
                  <div className="tdm-preview-loading">
                    <div className="tdm-spinner" />
                    <span>Chargement...</span>
                  </div>
                ) : previewUrl ? (
                  isImageFile(attachments[previewIndex]?.content_type || '') ? (
                    <img
                      src={previewUrl}
                      alt={attachments[previewIndex]?.file_name}
                      className="tdm-preview-image"
                    />
                  ) : isPdfFile(attachments[previewIndex]?.content_type || '') ? (
                    <iframe
                      src={previewUrl}
                      className="tdm-preview-pdf"
                      title={attachments[previewIndex]?.file_name}
                    />
                  ) : null
                ) : (
                  <div className="tdm-preview-error">
                    <DocumentIcon />
                    <span>Impossible de charger l'aperçu</span>
                  </div>
                )}
              </div>

              {/* Navigation Arrows */}
              {previewableAttachments.length > 1 && (
                <>
                  <button
                    className="tdm-preview-nav tdm-preview-nav-prev"
                    onClick={() => navigatePreview('prev')}
                    disabled={previewableAttachments.findIndex(a => a.id === attachments[previewIndex]?.id) === 0}
                    title="Précédent (←)"
                  >
                    <ChevronLeftIcon />
                  </button>
                  <button
                    className="tdm-preview-nav tdm-preview-nav-next"
                    onClick={() => navigatePreview('next')}
                    disabled={previewableAttachments.findIndex(a => a.id === attachments[previewIndex]?.id) === previewableAttachments.length - 1}
                    title="Suivant (→)"
                  >
                    <ChevronRightIcon />
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TaskDetailModal;
